import {
  LeadResearchCostRun,
  useLandlordResearchTrace,
  useLeadResearchCosts,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import { useState } from "react";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import CollapsibleCard from "../site/CollapsibleCard";

const usd = (n: number): string => `$${n.toFixed(2)}`;

const LeadResearchCostsCard = () => {
  const [days, setDays] = useState<number>(30);
  const costs = useLeadResearchCosts(days);
  const [traceOf, setTraceOf] = useState<LeadResearchCostRun | null>(null);

  const traceQuery = useLandlordResearchTrace(
    traceOf?.traceSubjectRef ?? null,
    traceOf?.traceRunId ?? null,
    traceOf !== null
  );

  return (
    <CollapsibleCard title="Landlord research costs" initialOpen={false}>
      <div className="d-flex flex-column gap-3" style={{ fontSize: 13 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Form.Label style={{ margin: 0, fontSize: 12, color: "#888" }}>
            Window
          </Form.Label>
          <Form.Select
            size="sm"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 160 }}
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
            <option value={365}>Last 365 days</option>
          </Form.Select>
          <span style={{ fontSize: 11, color: "#aaa" }}>
            Estimated from token + web-search counts (not billed).
          </span>
        </div>

        {costs.isLoading ? (
          <div style={{ color: "#888" }}>Loading costs...</div>
        ) : costs.isError || !costs.data ? (
          <Alert variant="danger">Could not load research costs.</Alert>
        ) : (
          <>
            <div style={{ display: "flex", gap: 24, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.totalUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  total ({costs.data.totals.runCount} runs,{" "}
                  {costs.data.totals.testRunCount} test)
                </div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.modelUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>model tokens</div>
              </div>
              <div>
                <div style={{ fontSize: 22, fontWeight: 600 }}>
                  {usd(costs.data.totals.webSearchUsd)}
                </div>
                <div style={{ fontSize: 11, color: "#888" }}>
                  web search ({costs.data.totals.webSearchCount} queries @{" "}
                  {usd(costs.data.webSearchUsdPerQuery)})
                </div>
              </div>
            </div>

            {costs.data.recent.length === 0 ? (
              <div style={{ fontSize: 12, color: "#666" }}>
                No research runs in this window.
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <Table size="sm" striped hover style={{ fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>Subject</th>
                      <th style={{ textAlign: "right" }}>Searches</th>
                      <th style={{ textAlign: "right" }}>Cost</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {costs.data.recent.map((r) => (
                      <tr key={r.id}>
                        <td>{new Date(r.createdAt).toLocaleString()}</td>
                        <td>
                          {r.subjectRef ?? "-"}
                          {r.isTest && (
                            <Badge bg="info" style={{ marginLeft: 4 }}>
                              test
                            </Badge>
                          )}
                          {r.failed && (
                            <Badge bg="secondary" style={{ marginLeft: 4 }}>
                              failed
                            </Badge>
                          )}
                        </td>
                        <td style={{ textAlign: "right" }}>{r.webSearchCount}</td>
                        <td style={{ textAlign: "right" }}>{usd(r.totalUsd)}</td>
                        <td>
                          {r.traceRunId && (
                            <Button
                              size="sm"
                              variant="link"
                              style={{ fontSize: 11, padding: 0 }}
                              onClick={() => setTraceOf(r)}
                            >
                              trace
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {costs.data.truncated && (
                  <div style={{ fontSize: 11, color: "#aaa" }}>
                    Showing the most recent {costs.data.recent.length} runs; totals
                    above cover the full window.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <Modal
          show={traceOf !== null}
          onHide={() => setTraceOf(null)}
          size="xl"
          scrollable
        >
          <Modal.Header closeButton>
            <Modal.Title style={{ fontSize: 18 }}>
              Research run - AI trace
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {traceQuery.isLoading ? (
              <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
            ) : traceQuery.data ? (
              <AITraceViewer trace={traceQuery.data} />
            ) : (
              <div style={{ fontSize: 12, color: "#888" }}>
                No AI trace available for this run.
              </div>
            )}
          </Modal.Body>
        </Modal>
      </div>
    </CollapsibleCard>
  );
};

export default LeadResearchCostsCard;
